import React from 'react';
import { StatCard } from '../ui/StatCard';

interface InventoryOverviewProps {
    totalImpressions: number;
    /** True when the avails summary has no computed value (stale/never-run refresh) — distinct from a genuine zero. */
    totalImpressionsUnavailable?: boolean;
    brandSafeUrls: number;
    matchRate: number;
    lastUpdated: string | null;
    loading?: boolean;
}

const InventoryOverviewPanel: React.FC<InventoryOverviewProps> = ({
    totalImpressions,
    totalImpressionsUnavailable = false,
    brandSafeUrls,
    matchRate,
    lastUpdated,
    loading = false,
}) => {
    const cards = buildCards(totalImpressions, totalImpressionsUnavailable, brandSafeUrls, matchRate, lastUpdated);

    return (
        <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {cards.map((card) => (
                    <StatCard
                        key={card.label}
                        testId={card.testId}
                        label={card.label}
                        value={loading ? <PulsePlaceholder /> : card.value}
                        caption={loading ? undefined : card.caption}
                        valueColorClassName={card.valueColorClassName}
                        variant={card.hero ? 'hero' : 'default'}
                    />
                ))}
            </div>
            <p className="text-xs text-ink-muted mt-2">
                Estimates based on recent traffic patterns
            </p>
        </div>
    );
};

function PulsePlaceholder() {
    return (
        <div className="animate-pulse space-y-2">
            <div className="h-5 bg-line rounded w-16" />
        </div>
    );
}

function buildCards(
    totalImpressions: number,
    totalImpressionsUnavailable: boolean,
    brandSafeUrls: number,
    matchRate: number,
    lastUpdated: string | null,
) {
    return [
        {
            testId: 'kpi-total-impressions',
            label: 'Total Impressions',
            value: totalImpressionsUnavailable ? '—' : formatCompact(totalImpressions),
            caption: totalImpressionsUnavailable ? 'Avails unavailable — refresh failed' : undefined,
            valueColorClassName: totalImpressionsUnavailable ? 'text-ink-muted opacity-45' : undefined,
            hero: true,
        },
        { testId: 'kpi-brand-safe-urls', label: 'Brand-Safe URLs', value: formatCount(brandSafeUrls) },
        { testId: 'kpi-match-rate', label: 'Match Rate', value: `${matchRate.toFixed(1)}%` },
        { testId: 'kpi-last-updated', label: 'Last Updated', value: formatRelativeTime(lastUpdated) },
    ];
}

function formatCompact(value: number): string {
    if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return String(value);
}

function formatCount(value: number): string {
    return value.toLocaleString();
}

function formatRelativeTime(iso: string | null): string {
    if (!iso) return 'Never';

    const diff = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;

    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
}

export default InventoryOverviewPanel;
export type { InventoryOverviewProps };
